import React from "react";
import { AlertTriangle, Loader2, RefreshCw, ShieldCheck } from "lucide-react";

type ReviewClassification =
  | "EXACT_MATCH"
  | "CANONICAL_VALUE_PRESERVED"
  | "SAFE_ENRICHMENT"
  | "NEW_VENDOR_CANDIDATE"
  | "REVIEW_REQUIRED"
  | "BLOCKING_IDENTITY_CONFLICT"
  | "CLOSED_REVIEW";

type ReviewDecision = {
  decisionId: string;
  classification: ReviewClassification;
  action: string;
  vendorCode?: string;
  businessNameMasked?: string;
  businessNumber?: { masked?: string };
  sourceRefs?: Array<{ collection: string; documentId: string }>;
  targetVendorId?: string;
  changedFields?: string[];
  conflictFields?: string[];
  reasons?: string[];
};

type DuplicateVendor = {
  vendorDocumentId: string;
  vendorCode: string;
  businessNameMasked: string;
  status: string;
  companyBranchIds: string[];
  completenessScore: number;
};

type DuplicateGroup = {
  groupId: string;
  businessNumberMasked: string;
  vendorCount: number;
  paymentReferenceCount: number;
  contractReferenceCount: number;
  recommendation: string;
  vendors: DuplicateVendor[];
};

export type VendorReviewData = {
  asOf: string;
  readOnly: boolean;
  automaticVendorWrites: number;
  classificationCounts: Record<string, number>;
  reviewDecisions: ReviewDecision[];
  duplicateBusinessNumberGroups: DuplicateGroup[];
  pseudoRows: Array<Record<string, unknown>>;
};

type Props = {
  view: "UNREGISTERED" | "DUPLICATES";
  data: VendorReviewData | null;
  loading: boolean;
  error: string;
  onLoad: () => void;
};

const CLASSIFICATION_LABELS: Record<ReviewClassification, string> = {
  EXACT_MATCH: "정확 일치",
  CANONICAL_VALUE_PRESERVED: "기존값 유지",
  SAFE_ENRICHMENT: "빈 필드 보완 후보",
  NEW_VENDOR_CANDIDATE: "신규 등록 후보",
  REVIEW_REQUIRED: "사람 검토 필요",
  BLOCKING_IDENTITY_CONFLICT: "식별 충돌",
  CLOSED_REVIEW: "폐업 검토",
};

const RECOMMENDATION_LABELS: Record<string, string> = {
  KEEP_SEPARATE_BRANCHES: "지점별 분리 유지 검토",
  SELECT_CANONICAL_REVIEW: "대표 canonical 지정 검토",
  INACTIVE_DUPLICATE_REVIEW: "비활성 중복 검토",
  IDENTITY_CONFLICT: "식별 충돌",
};

export default function VendorMasterReviewPanel({
  view,
  data,
  loading,
  error,
  onLoad,
}: Props) {
  const candidateRows = (data?.reviewDecisions || []).filter((row) =>
    view === "UNREGISTERED"
      ? ["NEW_VENDOR_CANDIDATE", "SAFE_ENRICHMENT", "REVIEW_REQUIRED", "CLOSED_REVIEW"].includes(
          row.classification
        )
      : row.classification === "BLOCKING_IDENTITY_CONFLICT"
  );

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="font-black text-slate-900">
              {view === "UNREGISTERED" ? "미등록 계약 거래처 검토" : "중복·충돌 검토"}
            </h4>
            <p className="mt-1 text-xs font-bold leading-5 text-slate-500">
              결과는 읽기 전용입니다. 후보는 자동 등록되지 않으며 기존 vendors 비공란 값도 변경하지 않습니다.
            </p>
          </div>
          <button
            type="button"
            onClick={onLoad}
            disabled={loading}
            className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-black text-white disabled:bg-slate-300"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            안전 검토 새로고침
          </button>
        </div>
        {error && (
          <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm font-bold text-red-700">
            {error}
          </p>
        )}
        {data && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-black">
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-800">
              자동 생성 {data.automaticVendorWrites}건
            </span>
            <span className="rounded-full bg-blue-50 px-3 py-1 text-blue-800">
              신규 후보 {data.classificationCounts.NEW_VENDOR_CANDIDATE || 0}건
            </span>
            <span className="rounded-full bg-red-50 px-3 py-1 text-red-800">
              식별 충돌 {data.classificationCounts.BLOCKING_IDENTITY_CONFLICT || 0}건
            </span>
          </div>
        )}
      </div>

      {view === "DUPLICATES" && data?.duplicateBusinessNumberGroups?.length ? (
        <div className="space-y-3">
          {data.duplicateBusinessNumberGroups.map((group) => (
            <div key={group.groupId} className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-black text-amber-950">
                  {group.businessNumberMasked} · {group.vendorCount}개 거래처
                </p>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-amber-800">
                  {RECOMMENDATION_LABELS[group.recommendation] || group.recommendation}
                </span>
              </div>
              <p className="mt-2 text-xs font-bold text-amber-800">
                결제 참조 {group.paymentReferenceCount}건 · 계약 참조 {group.contractReferenceCount}건
              </p>
              <div className="mt-3 grid gap-2 lg:grid-cols-2">
                {group.vendors.map((vendor) => (
                  <div key={vendor.vendorDocumentId} className="rounded-xl bg-white p-3 text-xs font-bold text-slate-700">
                    <p className="text-sm font-black text-slate-900">
                      [{vendor.vendorCode || "코드 미등록"}] {vendor.businessNameMasked || "상호 마스킹"}
                    </p>
                    <p className="mt-1">
                      상태 {vendor.status} · 사업장 {vendor.companyBranchIds.join(", ") || "미지정"} · 완성도 {vendor.completenessScore}/6
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {candidateRows.length > 0 ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {candidateRows.map((row) => (
            <div key={row.decisionId} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">
                  {CLASSIFICATION_LABELS[row.classification]}
                </span>
                {row.classification === "BLOCKING_IDENTITY_CONFLICT" ? (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                ) : (
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                )}
              </div>
              <p className="mt-3 font-black text-slate-900">
                [{row.vendorCode || "코드 미등록"}] {row.businessNameMasked || "상호 마스킹"}
              </p>
              <p className="mt-1 text-xs font-bold text-slate-500">
                사업자번호 {row.businessNumber?.masked || "미등록"} · 계약 원본 {row.sourceRefs?.length || 0}건
              </p>
              <p className="mt-2 text-xs font-bold leading-5 text-slate-700">
                {(row.reasons || []).join(" ") || "관리자 검토가 필요합니다."}
              </p>
              <p className="mt-2 text-[11px] font-bold text-slate-400">
                기존 거래처 연결 또는 신규 등록은 현재 거래처 등록 화면에서 재조회 후 수동 승인하세요.
              </p>
            </div>
          ))}
        </div>
      ) : data && view === "UNREGISTERED" ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm font-bold text-slate-500">
          검토가 필요한 미등록 계약 거래처가 없습니다.
        </p>
      ) : null}
    </div>
  );
}
